import { useSetState } from "components/ReduxProvider";
import React from "react";
import { useNavigate } from "react-router";
import { NOTIFICATION, NotificationEnum } from "../../sharedJs__generated/constants";
import { NOCARDSHASH } from "../dialogs/NoCardsDialog";
import { useEffectOnce, useEvent } from "react-use";
import { EVENT } from "tools/events";
import { useDBLog } from "hooks/useDBLog";
import { NOTIFICATIONDBLOG } from "sharedJs__generated/constants";
import { SUBSCRIPTION } from "../../root/profile/ProfileRouter.config";
import { MAIN, ONBOARDING } from "../../root/RootRouter.config";
import { useViewerQuery } from "../../queries/viewerQuery";
import { useOnboardingCompleted } from "../../hooks/useOnboardingCompleted";
import { StepScreenId } from "root/tutorial/onboarding/onboardingTypes";
import { getStepHash } from "root/tutorial/onboarding/OnboardingRedirect";
import { FIRSTSUBSCRIPTION } from "root/tutorial/misc/FirstSubscription";
import { useSeeHint } from "components/hint/Hint";
import useOnboardingData from "hooks/useOnboardingData";

const getCordovaMessaging = () => {
  return window.cordova?.plugins?.firebase?.messaging;
};

const NotificationsRegister: React.FC<{}> = () => {
  const { viewer, hintWasSeen } = useViewerQuery();
  const setFcmId = useSetState("fcmId");
  const navigate = useNavigate();
  const dbLog = useDBLog();
  const hasOnboardingCompleted = useOnboardingCompleted();
  const seeHint = useSeeHint();
  const { saveOnboardingData, getOnboardingData } = useOnboardingData();
  // this checks notification registration every time user opens app, it handles these cases:
  // - ask user for notifs permission after reinstall
  // - remove fcmId for users that has manually removed the permission in the OS
  // - ask for fcmId for users that has manually granted the permission to the OS
  useEffectOnce(() => {
    if (hasOnboardingCompleted && viewer && viewer.coachLevel !== NotificationEnum.none) {
      updateFcmId();
    }
  });

  useEvent(EVENT.refreshNotifications, () => {
    updateFcmId();
  });

  // handle what happens when user clicks a specific notification while the app is terminated
  useEffectOnce(() => {
    const messaging = getCordovaMessaging();
    if (!messaging) return;

    messaging.onBackgroundMessage((payload) => {
      // messaging.setBadge(1); // we might play (abtest) with icon badging in future
      if ([NOTIFICATION.sale, NOTIFICATION.christmasFamilyNotif, NOTIFICATION.expired].includes(payload.notifName)) {
        navigate(SUBSCRIPTION.url());
      }

      if (payload.notifName === NOTIFICATION.firstPaywallStuck) {
        if (!hintWasSeen(FIRSTSUBSCRIPTION)) {
          seeHint(FIRSTSUBSCRIPTION);
          const onboardingData = getOnboardingData();
          // only rewrite OnboardingData if user is still on first paywall
          if (onboardingData.latestStepReached === StepScreenId.FIRST_PAYWALL) {
            saveOnboardingData({
              latestStepReached: StepScreenId.PREPARED_COURSE,
              currentStep: StepScreenId.PREPARED_COURSE
            });
          }
          navigate(ONBOARDING.url() + getStepHash(StepScreenId.PREPARED_COURSE));
        }
      }

      if ([NOTIFICATION.noCards].includes(payload.notifName)) {
        navigate(MAIN.url() + NOCARDSHASH, { replace: true });
      }
      dbLog(NOTIFICATIONDBLOG, "notification opened", { notifName: payload.notifName });
    });
  });

  const updateFcmId = () => {
    const messaging = getCordovaMessaging();
    if (messaging && viewer && viewer.coachLevel !== NotificationEnum.none) {
      // we do call for the permission even if user has fcmId already - to detect if he disabled manually notifs, it will go to the catch clause
      // we can ask for permission multiple times, it only shows once by the OS
      messaging
        .requestPermission()
        .then(() => {
          messaging.getToken().then((fcmId) => {
            setFcmId(fcmId);
          });
        })
        .catch((d) => {
          setFcmId(null);
        });
      // messaging.onMessage(... here we can handle notifications when app is open...);
    }
  };

  return null;
};

export default NotificationsRegister;
